import { Deferred, Head } from "@inertiajs/react"
import { format } from "date-fns"

import { AgendaMarks } from "@/components/calls/agenda-marks"
import { REVIEW, reasonLabel } from "@/components/calls/copy"
import { LogCallForm, toLocalInput } from "@/components/calls/log-call-form"
import { Timeline } from "@/components/calls/timeline"
import { PageHeader } from "@/components/console/page-header"
import { StatusBadge, type StatusTier } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cx } from "@/components/ui/cx"
import { Notice } from "@/components/ui/notice"
import { Field, Fields, Panel } from "@/components/ui/panel"
import { AdminLayout } from "@/layouts/admin-layout"
import { mmss } from "@/lib/duration"
import { WritebackBadge } from "@/pages/admin/schools/_components/logged-calls-panel"
import type { AgendaItemState } from "@/pages/admin/schools/types"
import { adminCallListsPath, adminSchoolPath } from "@/routes"

import { adminCallReviewPath } from "./paths"
import type { ReviewProps, SummaryItem } from "./types"

const MARK_TIER: Record<AgendaItemState, StatusTier> = {
  open: "neutral",
  done: "confirmed",
  declined: "caution",
  skipped: "system",
}

const stamp = (iso: string) => format(new Date(iso), "d MMM HH:mm")

/*
 * The review screen: summary left, log form right, one primary — Log this
 * call. The page reads the Call row for everything the callbacks copied; the
 * timeline is the one live read. One submit writes one Task, and a second
 * submit is refused — there is no "correct it later" write.
 */
export default function AdminCallsReview({
  call,
  summary,
  marks,
  transcript,
  timeline,
  form,
}: ReviewProps) {
  const reviewed = call.review_state === "reviewed"
  const flagged = call.review_state === "flagged"
  const memoLost = flagged && call.failure_reason === "memo_lost"
  const noMemo = !summary && !flagged
  const spokeLikely = call.reached === "human"
  const facts = [
    call.contact?.name,
    `reached ${call.reached.replace("_", " ")}`,
    call.talk_seconds != null ? `talk ${mmss(call.talk_seconds)}` : null,
    call.memo_seconds != null
      ? `memo ${mmss(call.memo_seconds)}${call.memo_recovered ? " (called back)" : ""}`
      : null,
    stamp(call.called_at),
    `attempt ${call.attempt.step} of ${call.attempt.of}`,
  ]
    .filter(Boolean)
    .join(" · ")

  // The defaults are the one channel into the form: the backend seeds the
  // next step from the first follow-up only when its date has not passed.
  const initial = {
    salesforce_contact_id: form.defaults.salesforce_contact_id ?? "",
    disposition: form.defaults.disposition ?? "",
    reached: form.defaults.reached,
    talk_minutes: String(form.defaults.talk_minutes),
    called_at: toLocalInput(form.defaults.called_at),
    notes: form.defaults.notes,
    next_step: form.defaults.next_step,
    next_step_on: form.defaults.next_step_on ?? "",
  }

  const focusNotes = () => {
    const el = document.querySelector<HTMLTextAreaElement>(
      "#review-notes textarea",
    )
    el?.focus()
    el?.scrollIntoView({ block: "center", behavior: "smooth" })
  }

  const formPanel = reviewed ? (
    <Panel
      title="Logged"
      note={
        call.logged
          ? `by ${call.logged.by ?? "you"} · ${stamp(call.logged.at)}`
          : undefined
      }
      headingLevel="h3"
    >
      <Fields>
        <Field label="Disposition">
          {form.dispositions.find((d) => d.value === form.defaults.disposition)
            ?.label ?? form.defaults.disposition}
        </Field>
        <Field label="Reached">{call.reached.replace("_", " ")}</Field>
        {form.defaults.next_step_on && (
          <Field label="Follow-up">{form.defaults.next_step_on}</Field>
        )}
        <Field label="Salesforce">
          <WritebackBadge writeback={call.writeback} />
        </Field>
      </Fields>
      {call.writeback?.attention_line && (
        <Notice tone="caution" role="status">
          {call.writeback.attention_line}
        </Notice>
      )}
      <Notice tone="system">{REVIEW.secondSubmit}</Notice>
      <div className="flex justify-end">
        <Button size="lg" color="primary" isDisabled>
          Log this call
        </Button>
      </div>
    </Panel>
  ) : (
    <Panel
      title="Log this call"
      note={
        noMemo
          ? "attempt dispositions first"
          : memoLost
            ? undefined
            : REVIEW.prefilled
      }
      headingLevel="h3"
    >
      {noMemo && <Notice tone="system">{REVIEW.noMemo}</Notice>}
      <LogCallForm
        action={adminCallReviewPath(call.id)}
        paramKey="review"
        variant="review"
        initial={initial}
        contacts={form.contacts}
        dispositions={form.dispositions}
        reached_options={form.reached_options}
        cohorts={form.cohorts}
        terms={form.terms}
        attempt={call.attempt}
        suggestedDisposition={form.suggested_disposition}
        lockedContact={call.contact}
        attemptsFirst={!spokeLikely}
        notesNote={
          memoLost ? undefined : summary ? "seeded from the summary" : undefined
        }
        notesPlaceholder={
          memoLost
            ? "Write what you remember — this becomes the Task description."
            : undefined
        }
        submitLabel="Log this call"
      />
    </Panel>
  )

  return (
    <AdminLayout>
      <Head title={`Review · ${call.school.name}`} />
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div
          className={cx(
            "grid grid-cols-1 gap-8",
            !noMemo && "lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-10",
          )}
        >
          <section className="flex min-w-0 flex-col gap-6">
            <PageHeader
              crumbs={[
                { label: "call list", href: adminCallListsPath() },
                {
                  label: call.school.name,
                  href: adminSchoolPath(call.school.id),
                },
                { label: `call ${call.id}` },
                { label: "review" },
              ]}
              title={`Review — ${call.school.name}`}
              facts={facts}
            />

            {memoLost && (
              <Notice tone="caution" role="status">
                {REVIEW.memoLost}{" "}
                <span className="text-caution-700/80">
                  {REVIEW.memoLostAside}
                </span>
              </Notice>
            )}
            {flagged && !memoLost && (
              <Notice tone="caution" role="status">
                {reasonLabel(call.failure_reason, call.failure_reason_label)}.
                The call's facts are kept; log what happened.
              </Notice>
            )}

            {summary && (
              <>
                <Panel
                  title="Summary"
                  note={
                    summary.model
                      ? `trumpet · ${summary.model}`
                      : "trumpet, from your memo"
                  }
                >
                  <p className="text-secondary text-md leading-relaxed">
                    {summary.text}
                  </p>
                </Panel>

                <Panel
                  title="Against the agenda"
                  note={marksNote(summary.items)}
                >
                  <ul className="divide-tertiary flex flex-col divide-y">
                    {summary.items.map((item) => (
                      <SummaryRow
                        key={item.id}
                        item={item}
                        onAdd={focusNotes}
                      />
                    ))}
                  </ul>
                </Panel>

                {(summary.follow_ups.length > 0 ||
                  summary.spelling_corrections.length > 0) && (
                  <Panel title="Follow-ups" headingLevel="h3">
                    {summary.follow_ups.length > 0 ? (
                      <ul className="text-secondary flex flex-col gap-1 text-sm">
                        {summary.follow_ups.map((f, i) => (
                          <li key={i}>
                            {f.action}
                            {" — "}
                            <span className="notation">
                              {f.when ?? "no date said"}
                              {f.on ? ` · ${f.on}` : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-tertiary text-sm">None promised.</p>
                    )}
                    {summary.spelling_corrections.length > 0 && (
                      <p className="notation">
                        {summary.spelling_corrections
                          .map(
                            (s) => `heard “${s.heard}”, wrote “${s.corrected}”`,
                          )
                          .join(" · ")}
                      </p>
                    )}
                  </Panel>
                )}
              </>
            )}

            {!summary && flagged && marks.length > 0 && (
              <Panel
                title={REVIEW.fromMarks}
                note="a re-processed summary appears here if the operator recovers the audio"
                headingLevel="h3"
              >
                <AgendaMarks
                  callId={call.id}
                  items={marks}
                  head="Marks"
                  readOnly
                />
              </Panel>
            )}

            {transcript && (
              <Panel
                title="Transcript"
                note={[
                  transcript.seconds != null ? mmss(transcript.seconds) : null,
                  `${transcript.words} words`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                headingLevel="h3"
              >
                <details className="group">
                  <summary className="text-brand-secondary hover:text-brand-secondary_hover cursor-pointer list-none text-sm select-none">
                    <span className="group-open:hidden">
                      Show the transcript
                    </span>
                    <span className="hidden group-open:inline">
                      Hide the transcript
                    </span>
                  </summary>
                  <p className="text-secondary mt-3 text-sm whitespace-pre-line">
                    {transcript.text}
                  </p>
                </details>
              </Panel>
            )}

            <Panel
              title="Timeline"
              note="events · commands · transitions"
              headingLevel="h3"
            >
              <details className="group">
                <summary className="text-brand-secondary hover:text-brand-secondary_hover cursor-pointer list-none text-sm select-none">
                  <span className="group-open:hidden">Show the timeline</span>
                  <span className="hidden group-open:inline">
                    Hide the timeline
                  </span>
                </summary>
                <div className="mt-3">
                  <Deferred
                    data="timeline"
                    fallback={
                      <div
                        role="status"
                        aria-label="Fetching the timeline"
                        className="provisional-slot h-11"
                      />
                    }
                  >
                    <Timeline entries={timeline ?? []} />
                  </Deferred>
                </div>
              </details>
            </Panel>
          </section>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-8 lg:h-fit">
            {formPanel}
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}

const marksNote = (items: SummaryItem[]) => {
  const n = { done: 0, declined: 0, skipped: 0, open: 0, unmentioned: 0 }
  for (const i of items) {
    n[i.state] += 1
    if (i.unmentioned) n.unmentioned += 1
  }
  return [
    `${n.done} done`,
    n.declined ? `${n.declined} declined` : null,
    n.skipped ? `${n.skipped} skipped` : null,
    n.unmentioned ? `${n.unmentioned} unmentioned` : null,
  ]
    .filter(Boolean)
    .join(" · ")
}

/** One agenda item beside what the memo said about it. The unmentioned row is the one caution on the screen. */
function SummaryRow({ item, onAdd }: { item: SummaryItem; onAdd: () => void }) {
  return (
    <li
      className={cx(
        "flex flex-col gap-1 py-2.5",
        item.unmentioned && "caution-band -mx-3.5 px-3.5",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-primary text-sm font-medium">{item.label}</span>
        <StatusBadge
          tier={item.unmentioned ? "caution" : MARK_TIER[item.state]}
        >
          {item.unmentioned ? "unmentioned" : item.state}
        </StatusBadge>
      </div>
      {item.unmentioned ? (
        <p className="text-caution-700 text-sm">
          Not in the memo.{" "}
          <button
            type="button"
            onClick={onAdd}
            className="text-brand-secondary hover:text-brand-secondary_hover underline-offset-2 hover:underline"
          >
            Add what happened
          </button>{" "}
          if it was discussed.
        </p>
      ) : (
        <p className="text-secondary text-sm">
          {item.summary ??
            (item.state === "declined"
              ? "Marked declined during the call."
              : item.state === "skipped"
                ? "Skipped during the call; carries to the next attempt."
                : "")}
          {item.follow_up && (
            <span className="text-tertiary"> Follow-up: {item.follow_up}.</span>
          )}
        </p>
      )}
    </li>
  )
}
